import { useEffect, useMemo, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
} from "motion/react";
import { Magnetic } from "./Magnetic";
import { SocialIcon } from "./Icons";
import { profile } from "../../data/content";
import { EASE } from "../../lib/motion";
import { scrollToSection, useActiveSection } from "../../lib/scroll";

const links = [
  { label: "About", id: "about" },
  { label: "Skills", id: "skills" },
  { label: "Work", id: "work" },
  { label: "Journey", id: "journey" },
  { label: "Contact", id: "contact" },
] as const;

const ids = links.map((l) => l.id);

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(ids);

  const { scrollY, scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 28,
    mass: 0.3,
  });

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(y > 40);
    if (open) {
      setHidden(false);
      return;
    }
    if (y > 300 && y > prev + 1) setHidden(true);
    else if (y < prev - 1 || y < 140) setHidden(false);
  });

  // Lock the page while the mobile menu is open + close on Escape.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Never leave the menu stuck open when resizing up to desktop.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const close = () => {
      if (mq.matches) setOpen(false);
    };
    mq.addEventListener("change", close);
    return () => mq.removeEventListener("change", close);
  }, []);

  const go = useMemo(
    () => (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      setOpen(false);
      scrollToSection(id);
      window.history.replaceState(null, "", `#${id}`);
    },
    [],
  );

  return (
    <>
      {/* Scroll progress bar with a glowing head */}
      <motion.div
        className="fixed left-0 top-0 z-[60] h-[3px] w-full origin-left rounded-r-full bg-gradient-to-r from-primary via-secondary to-accent shadow-[0_0_14px_rgba(139,92,246,0.7)]"
        style={{ scaleX: progress }}
      />

      <motion.header
        initial="hidden"
        animate={hidden && !open ? "collapsed" : "visible"}
        variants={{
          hidden: { y: -90, opacity: 0 },
          visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.7, ease: EASE },
          },
          collapsed: {
            y: -110,
            opacity: 1,
            transition: { duration: 0.45, ease: EASE },
          },
        }}
        className={`fixed inset-x-0 top-0 z-[55] transition-[padding] duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <nav
          className={`mx-auto flex max-w-6xl items-center justify-between px-6 transition-all duration-500 md:px-10 ${
            scrolled
              ? "glass rounded-2xl py-3 shadow-lg shadow-black/40"
              : "py-2"
          }`}
        >
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            className="group flex items-center gap-2.5"
            data-cursor
          >
            <motion.span
              whileHover={{ rotate: -8, scale: 1.06 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
              className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-secondary font-display text-sm font-bold text-white shadow-lg shadow-primary/25"
            >
              {profile.initials}
            </motion.span>
            <span className="font-display text-sm font-semibold tracking-wide text-white/90">
              {profile.firstName}
              <span className="text-muted">.dev</span>
            </span>
          </a>

          <ul className="hidden items-center gap-1 md:flex">
            {links.map((link) => {
              const isActive = active === link.id;
              return (
                <li key={link.id} className="relative">
                  <a
                    href={`#${link.id}`}
                    onClick={(e) => go(e, link.id)}
                    aria-current={isActive ? "true" : undefined}
                    className={`group relative block rounded-full px-4 py-2 text-sm transition-colors duration-300 ${
                      isActive ? "text-white" : "text-white/65 hover:text-white"
                    }`}
                    data-cursor
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-pill"
                        className="absolute inset-0 -z-10 rounded-full border border-white/10 bg-white/[0.07]"
                        transition={{ type: "spring", stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span className="relative z-10">{link.label}</span>
                    <span className="absolute inset-x-3 bottom-1 h-px origin-left scale-x-0 bg-gradient-to-r from-primary to-accent transition-transform duration-300 group-hover:scale-x-100" />
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="hidden md:block">
            <Magnetic strength={0.3}>
              <a
                href={`mailto:${profile.email}`}
                className="group relative overflow-hidden rounded-full border border-white/15 bg-white/5 px-5 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:border-white/25 hover:bg-white/10"
                data-cursor
              >
                <span className="relative z-10">Hire Me</span>
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              </a>
            </Magnetic>
          </div>

          {/* Mobile toggle */}
          <button
            className="relative flex h-10 w-10 flex-col items-center justify-center gap-1.5 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-cursor
          >
            <motion.span
              animate={open ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="block h-0.5 w-6 rounded-full bg-white"
            />
            <motion.span
              animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.25 }}
              className="block h-0.5 w-6 origin-center rounded-full bg-white"
            />
            <motion.span
              animate={open ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="block h-0.5 w-6 rounded-full bg-white"
            />
          </button>
        </nav>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[54] md:hidden"
            initial="closed"
            animate="open"
            exit="closed"
          >
            <motion.div
              variants={{
                open: { clipPath: "circle(150% at calc(100% - 44px) 44px)" },
                closed: { clipPath: "circle(0% at calc(100% - 44px) 44px)" },
              }}
              transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
              className="absolute inset-0 bg-[#07080f]/97 backdrop-blur-2xl"
            >
              <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-primary/20 blur-[120px]" />
              <div className="pointer-events-none absolute -right-24 bottom-1/4 h-72 w-72 rounded-full bg-accent/15 blur-[120px]" />
            </motion.div>

            <div className="relative flex h-full flex-col justify-between px-8 pb-10 pt-28">
              <nav>
                <ul className="flex flex-col">
                  {links.map((link, i) => {
                    const isActive = active === link.id;
                    return (
                      <motion.li
                        key={link.id}
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{
                          duration: 0.55,
                          ease: EASE,
                          delay: 0.14 + i * 0.06,
                        }}
                        className="overflow-hidden border-b border-white/[0.07]"
                      >
                        <a
                          href={`#${link.id}`}
                          onClick={(e) => go(e, link.id)}
                          className="group flex items-baseline gap-4 py-4"
                          data-cursor
                        >
                          <span
                            className={`font-mono text-xs transition-colors ${
                              isActive ? "text-accent" : "text-muted"
                            }`}
                          >
                            0{i + 1}
                          </span>
                          <span
                            className={`font-display text-4xl font-bold tracking-tight transition-colors duration-300 ${
                              isActive
                                ? "text-gradient-animated"
                                : "text-white/80 group-hover:text-white"
                            }`}
                          >
                            {link.label}
                          </span>
                          <span
                            className={`ml-auto text-accent transition-all duration-300 ${
                              isActive
                                ? "translate-x-0 opacity-100"
                                : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                            }`}
                          >
                            →
                          </span>
                        </a>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.42, ease: EASE }}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  {profile.socials.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.label}
                      className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all duration-300 hover:-translate-y-1 hover:border-white/30 hover:text-white"
                      data-cursor
                    >
                      <SocialIcon name={s.icon} className="h-4 w-4" />
                    </a>
                  ))}
                </div>
                <a
                  href={`mailto:${profile.email}`}
                  className="font-mono text-xs text-muted transition-colors hover:text-white"
                  data-cursor
                >
                  {profile.email}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
